import { getCookie } from "@/lib/utils";
import type {
  CapacityLimitError,
  DeleteResponse,
  ErrorResponse,
  PaginatedResponse,
  RequestConfig,
} from "@/types/api";

export const baseUrl = "/admin/api";

/**
 * Error thrown when wallet balance is insufficient.
 */
export class InsufficientFundsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InsufficientFundsError";
  }
}

/**
 * Error thrown when capacity limit is exceeded.
 */
export class CapacityLimitExceededError extends Error {
  public readonly resource: string;
  public readonly resourceName: string;
  public readonly current: number;
  public readonly limit: number;
  public readonly upgradeUrl: string;
  public readonly addCapacityUrl: string;

  constructor(data: CapacityLimitError) {
    super(data.message);
    this.name = "CapacityLimitExceededError";
    this.resource = data.details.resource;
    this.resourceName = data.details.resource_name;
    this.current = data.details.current;
    this.limit = data.details.limit;
    this.upgradeUrl = data.actions.upgrade_url;
    this.addCapacityUrl = data.actions.add_capacity_url;
  }
}

/**
 * Base class for API services.
 */
export class ApiService {
  private pendingRequests = new Set<AbortController>();

  // TODO: modify this when we have a better error handling and alerting system
  protected formatError(error: ErrorResponse): string {
    if (typeof error === "object") {
      return Object.entries(error)
        .map(([field, messages]) => {
          if (Array.isArray(messages)) {
            return `${field}: ${messages.join(", ")}`;
          }
          return `${field}: ${messages}`;
        })
        .join("\n");
    }
    return String(error);
  }

  protected getHeaders(config: RequestConfig): Headers {
    const headers = new Headers(config.headers);
    const isFormData = config.body instanceof FormData;

    if (!isFormData) {
      headers.set("Content-Type", "application/json");
    }

    if (config.method !== "GET") {
      const csrfToken = getCookie("csrftoken");
      if (csrfToken) {
        headers.set("X-CSRFToken", csrfToken);
      }
    }

    return headers;
  }

  protected getUrl(url: string, params?: Record<string, string>): string {
    if (!params) return url;
    const searchParams = new URLSearchParams(params);
    return `${url}?${searchParams.toString()}`;
  }

  protected async request<T>(
    url: string,
    config: RequestConfig = {},
  ): Promise<T> {
    const controller = new AbortController();
    this.pendingRequests.add(controller);

    if (config.signal) {
      if (config.signal.aborted) {
        controller.abort();
      } else {
        config.signal.addEventListener("abort", () => controller.abort(), {
          once: true,
        });
      }
    }

    try {
      const response = await fetch(this.getUrl(url, config.params), {
        method: config.method || "GET",
        headers: this.getHeaders(config),
        body:
          config.body instanceof FormData
            ? config.body
            : config.body
              ? JSON.stringify(config.body)
              : undefined,
        signal: controller.signal,
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(errorData);

        if (response.status === 402) {
          if (errorData.error_code === "INSUFFICIENT_FUNDS") {
            throw new InsufficientFundsError(errorData.detail);
          }
          throw new CapacityLimitExceededError(errorData as CapacityLimitError);
        }

        throw new Error(this.formatError(errorData));
      }
      if (config.method === "DELETE") {
        return { success: true } as unknown as T;
      }
      const contentType = response.headers.get("content-type");
      if (contentType?.includes("application/json")) {
        return await response.json();
      } else if (contentType?.includes("text/")) {
        return (await response.text()) as unknown as T;
      } else {
        // For other types (binary, etc.), return as blob or handle as needed
        return (await response.blob()) as unknown as T;
      }
    } catch (err) {
      if (err instanceof Error && err.name !== "AbortError") {
        throw err;
      }
      throw err;
    } finally {
      this.pendingRequests.delete(controller);
    }
  }

  cancelAll() {
    this.pendingRequests.forEach((controller) => controller.abort());
    this.pendingRequests.clear();
  }
}

export abstract class CRUDApi<L, D> extends ApiService {
  protected abstract readonly BASE_URL: string;

  async list(params?: RequestConfig["params"]) {
    return this.request<PaginatedResponse<L>>(this.BASE_URL, {
      method: "GET",
      params: params,
    });
  }
  async retrieve(id: string) {
    return this.request<D>(`${this.BASE_URL}/${id}`);
  }
  async update(id: string, data: RequestConfig["body"]) {
    return this.request<D>(`${this.BASE_URL}/${id}`, {
      method: "PATCH",
      body: data,
    });
  }
  async create(data: RequestConfig["body"]) {
    return this.request<D>(this.BASE_URL, {
      method: "POST",
      body: data,
    });
  }
  async delete(id: string, data?: RequestConfig["body"]) {
    return this.request<DeleteResponse>(`${this.BASE_URL}/${id}`, {
      method: "DELETE",
      body: data,
    });
  }
}
